import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { get } from 'lodash';
import Router from 'next/router';
import dynamic from 'next/dynamic';
import { createStyles, withStyles, CircularProgress } from '@material-ui/core';
import EventEmitter from '../../lib/eventEmitter';
import { showSignupModal } from '../../actions/auth';
import {
  fetchMapFeed,
  fetchSavedSearches,
  modifyCityBoundaryHelper,
  setupMapBounds,
  updateFeedSearchHelper,
} from '../../actions/feed';
import { fetchFavorites } from '../../actions/favorite';
import { isMobile, isTablet, isWeb } from '../../lib/getDeviceSize';
import {
  localStorageTracker,
  promptHelper,
  getURLLocation,
} from '../../lib/helpers';
import { storageKeys } from '../../lib/storage';
import { FRIENDLY_URL_AREA } from '../../lib/constants';
import PageBase from '../../components/layout/PageBase';
import FeedMap from '../../components/pages/search/FeedMap';
import ListingsCarousel from '../../components/utility/ListingsCarousel';
import UndoToast from '../../components/pages/saved-searches/UndoToast';
const SortForm = dynamic(() =>
  import('../../components/pages/search/SortForm'),
);
const FilterForm = dynamic(() =>
  import('../../components/pages/search/FilterForm'),
);
const DeleteSavedSearchDialog = dynamic(() =>
  import('../../components/layout/DeleteSavedSearchDialog'),
);
import Listings from '../../components/utility/Listings';
import styles from '../../styles/sass/pages/Search.module.scss';
import { setFirstSearchCompleted } from '../../actions/dashboard';
import LogoLoader from '../../components/utility/LogoLoader';
import ListingModal from '../../components/pages/listing/listingModal';

export interface Props {
  classes: any;
  favorites: any[];
  firstSearchCompleted: boolean;
  feed: Object[];
  feedEmpty: boolean;
  feedFetched: boolean;
  feedSearch: any;
  fetchFavorites: Function;
  fetchSavedSearches: Function;
  fetchMapFeed: Function;
  initialMapLoad: boolean;
  modifyCityBoundaryHelper: Function;
  placeBoundaries: any;
  popularSearches: any;
  router: any;
  setupMapBounds: Function;
  showSignupModal: Function;
  sortBy: string;
  token: string;
  totalFilter: number;
  updateFeedSearchHelper: Function;
  usePlaceBoundary: boolean;
  dispatch: any;
}

export interface State {
  feed: any[];
  feedDirty: boolean;
  height: number;
  initialListing: any;
  isListingsView: boolean;
  selectedListingId: number | undefined;
  showListingsCarousel: boolean;
  width: number;
}

export const fabButtonStyles = createStyles({
  button: {
    display: 'flex',
    flexDirection: 'column',
  },
  buttonText: {
    color: '#323232',
    fontSize: 10,
    margin: 0,
    '&:hover': {
      color: '#2C2C2C',
    },
  },
  badgeIcon: {
    margin: '0 20px 0 -25px',
  },
});

const HEADER_HEIGHT = 70;
const FILTER_BAR_HEIGHT = 72;
const SORT_BAR_HEIGHT = 61; // Required only for tablet
const LISTING_HIGHLIGHT_DELAY = 2000;
const DEFAULT_TITLE =
  'Torii Homes: Search real estate listings and homes for sale in Massachusetts';
const DEFAULT_DESCRIPTION =
  "Modern home search that's all about you. Personalized listing recommendations powered by artificial intelligence. Save time and money when you buy with Torii.";

export const Search: React.FC<Props> = props => {
  const currentLocation = getURLLocation(props.router);

  const { area } = props.router.query;

  if (area && FRIENDLY_URL_AREA[area]) {
    const valuesToSubmit = FRIENDLY_URL_AREA[area];
    props.setupMapBounds(valuesToSubmit);
  }

  const [state, setState] = useState<State>({
    feed: [],
    feedDirty: true,
    height: 500,
    initialListing: undefined,
    isListingsView: false,
    selectedListingId: undefined,
    showListingsCarousel: false,
    width: 0,
  });

  useEffect(() => {
    EventEmitter.on('feedLoaded', highlightListing);
    const { token } = props;
    fetchLocationFromQuery();
    if (typeof window !== 'undefined') {
      updateWindowDimensions();
      window.addEventListener('resize', updateWindowDimensions);
    }
    scrollToTop();
    return () => {
      window.removeEventListener('resize', updateWindowDimensions);
      EventEmitter.removeListener('feedLoaded', highlightListing);
    };
  }, []);

  useEffect(() => {
    if (props.token) {
      props.fetchFavorites(props.token);
      props.fetchSavedSearches();
      if (!props.firstSearchCompleted) {
        props.dispatch(setFirstSearchCompleted());
      }
    }
  }, [props.token]);

  useEffect(() => {
    EventEmitter.on('onMarkerClicked', _onMarkerClicked);
    EventEmitter.on('onMapClick', _onMapClick);
    return () => {
      EventEmitter.removeListener('onMarkerClicked', _onMarkerClicked);
      EventEmitter.removeListener('onMapClick', _onMapClick);
    };
  }, [props.router]);

  const scrollToTop = useCallback(() => {
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo(0, 0);
    }
  }, []);

  const updateQueryLocation = useCallback(() => {
    if (props.router.query.location) {
      props.updateFeedSearchHelper({
        ...props.feedSearch,
        location: props.router.query.location,
      });
    }
  }, [props.feedSearch, props.router.query]);

  const highlightListing = () => {
    const view = get(props.router, 'query.view', undefined);
    if (!view || (view && view === 'map')) {
      if (state.selectedListingId) {
        setTimeout(() => {
          EventEmitter.emit('highlightListing', {
            selectedListingId: state.selectedListingId,
          });
        }, LISTING_HIGHLIGHT_DELAY);
      }
    }
  };

  const fetchLocationFromQuery = () => {
    const filters = JSON.parse(get(props.router, 'query.filters', '{}'));
    const view = get(props.router, 'query.view', undefined);
    const selectedListingId = get(
      props.router,
      'query.selectedListingId',
      undefined,
    );
    if (Object.keys(filters).length > 0) {
      if (!filters.locationName) {
        cityBoundaryHelper(false);
      }
      props.updateFeedSearchHelper(filters);
    } else {
      updateQueryLocation();
    }
    setState((previousState: State) => {
      return {
        ...previousState,
        selectedListingId,
      };
    });
  };

  const loadMapFeed = (
    mapCenter: string,
    boundingBox: string,
    polygonBoundaries: string | undefined,
    useBoundingBox: boolean,
    zoom: number,
    fetchLargePhotoList: boolean = false,
    mapView: boolean = true,
  ) => {
    const { feedSearch, token, usePlaceBoundary } = props;
    if (!fetchLargePhotoList) {
      setState((previousState: State) => {
        return {
          ...previousState,
          feedDirty: true,
        };
      });
    }
    if (!token) {
      localStorageTracker(
        storageKeys.noOfSearches,
        3,
        () => {
          props.fetchMapFeed(
            mapCenter,
            token,
            boundingBox,
            useBoundingBox,
            { ...feedSearch, zoom, polygonBoundaries, area },
            usePlaceBoundary,
            fetchLargePhotoList,
            mapView,
          );
        },
        () => {
          promptHelper(
            storageKeys.homeVisitDate,
            props.showSignupModal(props.router.pathname),
          );
        },
      );
    } else {
      props.fetchMapFeed(
        mapCenter,
        token,
        boundingBox,
        useBoundingBox,
        { ...feedSearch, zoom, polygonBoundaries, area },
        usePlaceBoundary,
        fetchLargePhotoList,
        mapView,
      );
    }
  };

  const _onMarkerClicked = (listing: any) => {
    setState((previousState: State) => {
      return {
        ...previousState,
        initialListing: listing,
        showListingsCarousel: true,
      };
    });
    const query = props.router.query;
    query.selectedListingId = listing.id;
    Router.push({
      pathname: '/search',
      query,
    });
  };

  const _onMapClick = () => {
    setState((previousState: State) => {
      return {
        ...previousState,
        initialListing: undefined,
        showListingsCarousel: false,
      };
    });
    const query = props.router.query;
    if (query.selectedListingId) {
      delete query.selectedListingId;
      Router.push({
        pathname: '/search',
        query,
      });
    }
  };

  const cityBoundaryHelper = useCallback((usePlaceBoundary: boolean) => {
    props.modifyCityBoundaryHelper(usePlaceBoundary);
  }, []);

  const updateWindowDimensions = useCallback(() => {
    setState((previousState: State) => {
      return {
        ...previousState,
        height: window.innerHeight,
        width: window.innerWidth,
      };
    });
  }, []);

  const renderFilterSort = useCallback(() => {
    const { width } = state;
    const mobile = width <= 529;
    return (
      <SortForm mobile={mobile} usePlaceBoundary={props.usePlaceBoundary} />
    );
  }, [props.usePlaceBoundary]);

  const feedContents = () => {
    const {
      placeBoundaries,
      feed,
      feedSearch,
      feedFetched,
      favorites,
      initialMapLoad,
      popularSearches,
    } = props;
    const {
      height,
      initialListing,
      isListingsView,
      showListingsCarousel,
      width,
    } = state;
    const mobile = width <= 529;
    const innerHeight =
      height -
      HEADER_HEIGHT -
      FILTER_BAR_HEIGHT -
      (isTablet() ? SORT_BAR_HEIGHT : 0);
    const locationName = feedSearch.locationName;
    const photosByRoom = feedSearch.photosByRoom;
    let searchState: 'MA' | undefined;
    if (locationName) {
      searchState = locationName.includes(', MA') ? 'MA' : undefined;
    }
    return (
      <div className={`page-container ${styles.feed} feed`}>
        <div
          style={{
            display: 'flex',
            border: '1px solid #bdbdbd',
            height: FILTER_BAR_HEIGHT,
          }}
        >
          <FilterForm
            key={feedSearch.locationName}
            mobile={isMobile()}
            deviceHeight={height}
            router={props.router}
          />
        </div>
        <div
          className={`row col-xs-12 ${styles.searchResultContainer} search-result-container`}
          style={{ height: innerHeight }}
        >
          {(!mobile || !isListingsView) && (
            <div className={`col-sm-4 ${styles.mapContainer}`}>
              {Object.keys(feedSearch).length > 0 ? (
                <FeedMap
                  allowDrawing={isWeb()} // Allow drawing only on web for now
                  favorites={favorites}
                  feed={feed}
                  feedSearch={feedSearch}
                  initialMapLoad={initialMapLoad}
                  loadMapFeed={loadMapFeed}
                  mapHeight={innerHeight}
                  placeBoundaries={placeBoundaries}
                  routerLocation={props.router.query.location}
                  toggleCityBoundary={cityBoundaryHelper}
                />
              ) : (
                <div className={styles.dataLoader}>
                  <CircularProgress
                    size={40}
                    thickness={2}
                    variant="indeterminate"
                  />
                </div>
              )}
            </div>
          )}
          <div
            className={`col-sm-8 ${styles.listingsContainer} listings-container`}
            style={!mobile ? { height: '100%' } : {}}
          >
            <div className={`col-xs-12 ${styles.searchForms}`}>
              {renderFilterSort()}
            </div>
            {!feedFetched && (
              <div className={styles.dataLoader}>
                <LogoLoader imageProps={{ height: 200, width: 200 }} />
              </div>
            )}
            {feedFetched && (!mobile || isListingsView) && (
              <div
                className={styles.listingsContent}
                style={{ height: innerHeight - 62 }}
              >
                <Listings
                  key={`${props.sortBy} ${feed.length}`}
                  allowFavorite
                  listings={feed}
                  narrow
                  searchState={searchState}
                  popularSearches={popularSearches}
                  photosByRoom={photosByRoom}
                  showPopularSearches
                  showDelete={true}
                  showInModal={true}
                  pointDialogToSidebar={true}
                />
              </div>
            )}
          </div>
        </div>
        {mobile && feedFetched && showListingsCarousel && (
          <div
            className={`${styles.carouselListingContainer} carousel-listing-container mobile-only`}
          >
            <ListingsCarousel
              initialListing={initialListing}
              router={props.router}
              listings={feed}
            />
          </div>
        )}
        <ListingModal />
      </div>
    );
  };

  return (
    <PageBase
      title={
        currentLocation
          ? `Homes for sale in ${currentLocation}: Torii Homes`
          : DEFAULT_TITLE
      }
      overrideRemarks={
        currentLocation
          ? `${
              props.feed.length ? `${props.feed.length} ` : ''
            }Homes for sale in ${currentLocation}. ${DEFAULT_DESCRIPTION}`
          : DEFAULT_DESCRIPTION
      }
    >
      {feedContents()}
      <DeleteSavedSearchDialog />
      <UndoToast />
    </PageBase>
  );
};

const mapStateToProps = (state: any) => ({
  favorites: state.favorites.favorites,
  feed: get(state, 'feed.feed', []),
  feedEmpty: state.feed.feedEmpty,
  feedFetched: state.feed.feedFetched,
  feedSearch: state.feed.feedSearch,
  initialMapLoad: state.feed.initialMapLoad,
  placeBoundaries: state.feed.placeBoundaries,
  popularSearches: state.feed.popularSearchAreas,
  sortBy: state.feed.sortBy,
  token: state.auth.token,
  totalFilter: state.feed.totalFilter,
  usePlaceBoundary: state.feed.usePlaceBoundary,
  firstSearchCompleted: state.dashboard.firstSearchCompleted,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchFavorites: (
    token: string,
    offset: number = 0,
    limit: number = 100,
    reset: boolean = true,
  ) => dispatch(fetchFavorites(token, offset, limit, reset)),
  fetchMapFeed: (
    mapCenter: string,
    token: string,
    boundingBox: string,
    useBoundingBox: boolean,
    feedSearch: any,
    usePlaceBoundary: boolean,
    fetchLargePhotoList: boolean,
    mapView: boolean,
  ) =>
    dispatch(
      fetchMapFeed(
        mapCenter,
        token,
        boundingBox,
        useBoundingBox,
        feedSearch,
        usePlaceBoundary,
        fetchLargePhotoList,
        mapView,
      ),
    ),
  modifyCityBoundaryHelper: (usePlaceBoundary: boolean) =>
    dispatch(modifyCityBoundaryHelper(usePlaceBoundary)),
  setupMapBounds: (values: any) => dispatch(setupMapBounds(values, true)),
  showSignupModal: (postAuthPath: string) =>
    dispatch(showSignupModal(postAuthPath)),
  updateFeedSearchHelper: (feedSearch: any) =>
    dispatch(updateFeedSearchHelper(feedSearch)),
  fetchSavedSearches: () => dispatch(fetchSavedSearches()),
  dispatch,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(fabButtonStyles)(withRouter(Search)));
